import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  PauseCircle,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import {
  getDomainCheckJobsAdmin,
  type DomainCheckJobsAdminData,
} from "@/lib/domain-check-jobs-admin.functions";

export const Route = createFileRoute("/_authenticated/domain-check-jobs")({
  component: DomainCheckJobsPage,
  head: () => ({ meta: [{ title: "Domain-Check Jobs — InboxPulse" }] }),
});

function fmtDuration(ms: number | null) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function fmtTime(iso: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "2-digit",
  });
}

function fmtAge(iso: string | null) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diff) || diff < 0) return "—";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

function statusBadge(status: string) {
  switch (status) {
    case "done":
      return (
        <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15 border border-emerald-500/30">
          done
        </Badge>
      );
    case "running":
      return (
        <Badge className="bg-blue-500/15 text-blue-700 hover:bg-blue-500/15 border border-blue-500/30">
          running
        </Badge>
      );
    case "pending":
      return (
        <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/15 border border-amber-500/30">
          pending
        </Badge>
      );
    case "failed":
      return (
        <Badge className="bg-destructive/15 text-destructive hover:bg-destructive/15 border border-destructive/30">
          failed
        </Badge>
      );
    case "canceled":
      return (
        <Badge className="bg-muted text-muted-foreground border border-border">canceled</Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function DomainCheckJobsPage() {
  const fetchData = useServerFn(getDomainCheckJobsAdmin);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["domain-check-jobs-admin"],
    queryFn: () => fetchData(),
    refetchInterval: 15_000,
  });

  const isAdminError = error instanceof Error && /forbidden|admin/i.test(error.message);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Domain-Check Jobs"
        description="Live-Status der Domain-Check-Warteschlange, letzte Jobs und Verlauf der jüngsten Reputation-Checks."
        actions={
          <button
            onClick={() => refetch()}
            className="text-sm px-3 py-1.5 rounded-md border border-border hover:bg-accent transition disabled:opacity-50"
            disabled={isFetching}
          >
            {isFetching ? "Aktualisiere…" : "Aktualisieren"}
          </button>
        }
      />

      <HelpPanel
        title="Hilfe: Was prüft das System gerade?"
        what={
          <>
            Hier siehst du die <b>Warteschlange aller laufenden Domain-Checks</b> (DKIM, SPF, DMARC,
            Blacklist) und die zuletzt fertig gewordenen Prüfungen.
          </>
        }
        why={
          <>
            Wenn eine Domain plötzlich "rot" anzeigt, kannst du hier sehen, wann sie zuletzt geprüft
            wurde und ob das System gerade nochmal nachschaut.
          </>
        }
        how={[
          <>Aktualisiert sich automatisch — neue Jobs erscheinen oben.</>,
          <>
            Status: <b>pending</b> = wartet, <b>running</b> = läuft, <b>done</b> = fertig,{" "}
            <b>error</b> = Problem.
          </>,
        ]}
        tips={[
          <>
            Viele "error" → der Domain-Provider blockiert vielleicht unsere Anfragen. Admin
            informieren.
          </>,
        ]}
        glossary={[
          {
            term: "Job",
            def: 'Eine Aufgabe für das System, z.B. "prüfe Domain X". Wird abgearbeitet, dann archiviert.',
          },
        ]}
      />

      {isLoading && <Card className="p-6 text-muted-foreground">Lade Jobs…</Card>}

      {error && (
        <Card className="p-6 border-destructive/50 bg-destructive/5 text-destructive flex items-start gap-3">
          <AlertTriangle className="size-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">
              {isAdminError ? "Admin-Zugriff erforderlich" : "Fehler beim Laden"}
            </div>
            <div className="text-sm opacity-80 mt-1">
              {error instanceof Error ? error.message : String(error)}
            </div>
          </div>
        </Card>
      )}

      {!isLoading && !error && data && <Body data={data} />}
    </div>
  );
}

function Body({ data }: { data: DomainCheckJobsAdminData }) {
  const { statusCounts, oldestPendingAt, recentJobs, recentHistory } = data;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Tile icon={PauseCircle} label="Pending" value={statusCounts.pending} tone="warn" />
        <Tile icon={Loader2} label="Running" value={statusCounts.running} tone="info" />
        <Tile icon={CheckCircle2} label="Done" value={statusCounts.done} tone="success" />
        <Tile icon={XCircle} label="Failed" value={statusCounts.failed} tone="danger" />
        <Tile icon={Clock} label="Ältester pending" value={fmtAge(oldestPendingAt)} mono />
      </div>

      <Card className="p-0 overflow-hidden mb-8">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold">Letzte 50 Jobs</h3>
          <span className="text-xs text-muted-foreground">
            Stand {new Date(data.generatedAt).toLocaleTimeString()}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Domain</th>
                <th className="px-4 py-2 font-medium text-right">Versuche</th>
                <th className="px-4 py-2 font-medium">Enqueued</th>
                <th className="px-4 py-2 font-medium">Started</th>
                <th className="px-4 py-2 font-medium">Finished</th>
                <th className="px-4 py-2 font-medium text-right">Dauer</th>
                <th className="px-4 py-2 font-medium text-right">Score</th>
                <th className="px-4 py-2 font-medium">Fehler</th>
              </tr>
            </thead>
            <tbody>
              {recentJobs.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center text-muted-foreground">
                    Keine Jobs vorhanden.
                  </td>
                </tr>
              )}
              {recentJobs.map((j) => (
                <tr key={j.id} className="border-t border-border/60 hover:bg-muted/30">
                  <td className="px-4 py-2">{statusBadge(j.status)}</td>
                  <td className="px-4 py-2 font-mono text-xs">{j.domain}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {j.attempts}/{j.max_attempts}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {fmtTime(j.enqueued_at)}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {fmtTime(j.started_at)}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {fmtTime(j.finished_at)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {fmtDuration(j.duration_ms)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{j.result_score ?? "—"}</td>
                  <td className="px-4 py-2 text-xs text-destructive max-w-[260px] truncate">
                    {j.last_error ?? ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b">
          <h3 className="font-semibold">Letzte Reputation-Checks (30)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Zeit</th>
                <th className="px-4 py-2 font-medium">Domain</th>
                <th className="px-4 py-2 font-medium text-right">Score</th>
                <th className="px-4 py-2 font-medium">MX</th>
                <th className="px-4 py-2 font-medium">SPF</th>
                <th className="px-4 py-2 font-medium">DMARC</th>
                <th className="px-4 py-2 font-medium">Blacklists</th>
              </tr>
            </thead>
            <tbody>
              {recentHistory.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    Noch keine Checks gespeichert.
                  </td>
                </tr>
              )}
              {recentHistory.map((h) => (
                <tr key={h.id} className="border-t border-border/60 hover:bg-muted/30">
                  <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {fmtTime(h.checked_at)}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{h.domain ?? h.domain_id}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold">{h.score}</td>
                  <td className="px-4 py-2">{h.mx ? "✓" : "—"}</td>
                  <td className="px-4 py-2">{h.spf ? "✓" : "—"}</td>
                  <td className="px-4 py-2">
                    {h.dmarc ? `✓ ${h.dmarc_policy ?? ""}`.trim() : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {h.blacklisted_zones.length === 0 ? (
                      <span className="text-emerald-600 text-xs">sauber</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-destructive text-xs font-medium">
                        <ShieldAlert className="size-3.5" />
                        {h.blacklisted_zones.join(", ")}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function Tile({
  icon: Icon,
  label,
  value,
  tone,
  mono,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  tone?: "success" | "danger" | "warn" | "info";
  mono?: boolean;
}) {
  const toneCls =
    tone === "success"
      ? "text-emerald-600"
      : tone === "danger"
        ? "text-destructive"
        : tone === "warn"
          ? "text-amber-600"
          : tone === "info"
            ? "text-blue-600"
            : "text-foreground";
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className="size-4" />
        {label}
      </div>
      <div className={`text-2xl font-semibold mt-1 ${toneCls} ${mono ? "tabular-nums" : ""}`}>
        {value}
      </div>
    </Card>
  );
}
